import { Config } from "@/components/crud/crud-config";
import CrudFormElements from "@/components/crud/crud-form-elements";
import CrudPage from "@/components/crud/page";
import PencilIcon from "@/components/icons/pencil";
import TableActions from "@/components/table-actions";
import PermissionsLists from "@/config/PermissionsLists";
import { EndPoints, httpService } from "@/Core/Client";
import {
    VenueFormDto,
    VenueResponseDto,
} from "@/Dtos/venue-dto";
import DateCell from "@/ui/date-cell";
import { useQueryClient } from "@tanstack/react-query";
import { createColumnHelper } from "@tanstack/react-table";
import { useState } from "react";
import { Checkbox } from "rizzui";
import { useModal } from "../shared/modal-views/use-modal";
import EyeIcon from "@/components/icons/eye";
import routes from "@/routers/routes";
import { useNavigate } from "react-router-dom";
import DollarIcon from "@/components/icons/dollar";

const config: Config<VenueResponseDto, VenueFormDto> = {
    endPoint: EndPoints.venues.crud,
    queryKey: ["venues"],
    columns: [],
    fields: [],
    formPageRoute: routes.venues.create,
    titles: {
        pageTitle: "Venues",
        pageCreateTitle: "Add Venue",
        pageEditTitle: "Edit Venue",
    },
    filterProps: {
        useDrawer: false,
        sortingClassName: "w-auto",
        directionClassName: "w-auto",
    },
    permissionsCreate: PermissionsLists.Venues.Create,
    formOptions: {
        customSize: "1000px",
    },
};

const columnHelper = createColumnHelper<VenueResponseDto>();

export const columns = [
    columnHelper.display({
        id: "select",
        size: 50,
        header: ({ table }) => (
            <Checkbox
                className="ps-3.5"
                aria-label="Select all Rows"
                checked={table.getIsAllPageRowsSelected()}
                onChange={() => table.toggleAllPageRowsSelected()}
            />
        ),
        cell: ({ row }) => (
            <Checkbox
                className="ps-3.5"
                aria-label="Select Row"
                checked={row.getIsSelected()}
                onChange={row.getToggleSelectedHandler()}
            />
        ),
    }),
    columnHelper.accessor("name", {
        id: "name",
        size: 200,
        header: "Name",
        cell: ({ row }) => row.original.name,
    }),
    columnHelper.accessor("entity.name", {
        id: "entity.name",
        size: 200,
        header: "Company",
        cell: ({ row }) => row.original.entity?.name,
    }),
    columnHelper.accessor("capacity", {
        id: "capacity",
        size: 200,
        header: "Capacity",
        cell: ({ row }) => row.original.capacity.format(),
    }),
    columnHelper.accessor("price", {
        id: "price",
        size: 200,
        header: "Booking Price",
        cell: ({ row }) =>
            row.original.price.toCurrency() + " " + (row.original.priceType === "fixed" ? "Fixed" : "Per Head"),
    }),
    columnHelper.accessor("remarks", {
        id: "remarks",
        size: 200,
        header: "Remarks",
        cell: ({ row }) => row.original.remarks,
    }),
    columnHelper.accessor("createdAt", {
        id: "createdAt",
        size: 200,
        header: "Created",
        cell: ({ row }) => (
            <DateCell
                date={new Date(Date.parse(row.original?.createdAt ?? ""))}
            />
        ),
    }),
    columnHelper.display({
        id: "actions",
        size: 80,
        cell: ({ row }) => {
            const { openModal } = useModal();
            const [isLoading, setLoading] = useState(false);
            const queryClient = useQueryClient();
            const navigate = useNavigate();

            return (
                <TableActions
                    row={row.original}
                    onDelete={() => {
                        setLoading(true);
                        httpService(config.endPoint).delete(row.original.id!);
                        queryClient.refetchQueries({
                            queryKey: config.queryKey,
                        });
                        setLoading(false);
                    }}
                    actions={[
                        {
                            title: "Details",
                            color: "gray",
                            isLoading: isLoading,
                            icon: <EyeIcon className="size-4" />,
                            onClick: () =>
                                openModal({
                                    view: <CrudFormElements<VenueFormDto, VenueResponseDto>
                                        editData={row.original as any}
                                        config={config as any}
                                    />,
                                    customSize: config.formOptions?.customSize,
                                }),
                        },
                        {
                            title: "Edit",
                            color: "gray",
                            isLoading: isLoading,
                            icon: <PencilIcon className="size-4" />,
                            onClick: () => navigate(routes.venues.edit(row.original.id), { state: row.original }),
                        },
                        {
                            title: "Payments",
                            color: "green",
                            isLoading: isLoading,
                            icon: <DollarIcon className="size-4" />,
                            onClick: () => navigate(routes.venues.payments(row.original.id)),
                        }
                    ]}
                />
            );
        },
    }),
];

config.columns = columns;

export default function MainPage() {
    return CrudPage<VenueResponseDto, VenueFormDto>({ config: config });
}